import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Tooltip
} from "@mui/material";
import {
  X,
  Camera,
  RotateCw,
  MapPin,
  AlertTriangle
} from "lucide-react";
import { stampGeoTagFromVideo, isLaptopOrDesktopDevice } from "./geoTagWatermarkUtil";

const LiveCameraModal = ({
  open,
  onClose,
  onPhotoCaptured,
  gpsCoords,
  instituteName = "EduSkills Institute",
  domainTitle = "Event Photos",
  eventTypeLabel = "FDP"
}) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const [facingMode, setFacingMode] = useState("environment");
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [isCameraStarting, setIsCameraStarting] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [isCapturing, setIsCapturing] = useState(false);
  const [shutterFlash, setShutterFlash] = useState(false);
  const [currentClockTime, setCurrentClockTime] = useState("");

  const stopCamera = () => {
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => {
          try {
            track.stop();
            track.enabled = false;
          } catch (e) {}
        });
        streamRef.current = null;
      }
      if (videoRef.current) {
        try {
          videoRef.current.pause();
        } catch (e) {}
        videoRef.current.srcObject = null;
      }
    } catch (e) {
      console.warn("Error stopping camera stream:", e);
    } finally {
      setIsCameraActive(false);
      setIsCameraStarting(false);
      setIsCapturing(false);
      setCameraError("");
    }
  };

  const startCamera = async (targetFacing = facingMode) => {
    setIsCameraStarting(true);
    setCameraError("");

    if (isLaptopOrDesktopDevice()) {
      setCameraError(
        "Camera capture is restricted on laptops and desktop computers. Please access this portal on a mobile device or smartphone to capture on-site geo-tagged photos."
      );
      setIsCameraStarting(false);
      return;
    }
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => {
          try {
            track.stop();
          } catch (e) {}
        });
        streamRef.current = null;
      }

      const constraints = {
        video: {
          facingMode: { ideal: targetFacing },
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
        audio: false
      };

      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia(constraints);
      } catch (idealErr) {
        stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      }

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        try {
          await videoRef.current.play();
        } catch (playErr) {
          console.warn("Video play warning:", playErr);
        }
      }
      setIsCameraActive(true);
    } catch (err) {
      console.error("Camera access error:", err);
      let msg = "Failed to access camera.";
      if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
        msg = "Camera permission denied. Please allow camera access in browser settings.";
      } else if (err.name === "NotFoundError" || err.name === "DevicesNotFoundError") {
        msg = "No camera found on this device.";
      } else if (err.name === "NotReadableError") {
        msg = "Camera is already in use by another application.";
      }
      setCameraError(msg);
    } finally {
      setIsCameraStarting(false);
    }
  };

  const handleSwitchCamera = async () => {
    const nextFacing = facingMode === "environment" ? "user" : "environment";
    setFacingMode(nextFacing);
    await startCamera(nextFacing);
  };

  const handleClose = () => {
    stopCamera();
    onClose();
  };

  // Start/Stop camera stream with dialog visibility
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        startCamera(facingMode);
      }, 150);
      return () => clearTimeout(timer);
    } else {
      stopCamera();
    }
  }, [open]);

  // Clean up stream on unmount
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  // Live ticking clock
  useEffect(() => {
    if (!open) return;
    const updateClock = () => {
      const now = new Date();
      setCurrentClockTime(
        `${now.toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric"
        })} • ${now.toLocaleTimeString("en-IN", {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit"
        })}`
      );
    };
    updateClock();
    const interval = setInterval(updateClock, 1000);
    return () => clearInterval(interval);
  }, [open]);

  // Capture Photo Handler
  const handleCapture = async () => {
    if (!videoRef.current || isCapturing || !isCameraActive) return;

    setIsCapturing(true);
    setShutterFlash(true);
    setTimeout(() => setShutterFlash(false), 200);

    try {
      const blob = await stampGeoTagFromVideo(videoRef.current, gpsCoords, {
        instituteName,
        domainTitle,
        eventType: eventTypeLabel,
        address: gpsCoords?.address || ""
      });

      stopCamera();
      if (onPhotoCaptured) {
        onPhotoCaptured(blob, gpsCoords);
      }
      onClose();
    } catch (err) {
      console.error("Capture failed:", err);
      setCameraError("Failed to capture photo: " + (err.message || "Unknown error"));
    } finally {
      setIsCapturing(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: "#0b0f17",
          color: "#ffffff",
          borderRadius: { xs: "8px", sm: "16px" },
          overflow: "hidden",
          m: { xs: 1, sm: 2 },
          maxHeight: "96vh",
          boxShadow: "0 24px 48px rgba(0,0,0,0.5)"
        }
      }}
    >
      {/* Top Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: { xs: 2, sm: 2.5 },
          py: 1.5,
          borderBottom: "1px solid rgba(255,255,255,0.1)",
          backgroundColor: "#0f172a"
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "8px",
              backgroundColor: "rgba(56, 189, 248, 0.15)",
              color: "#38bdf8",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1px solid rgba(56, 189, 248, 0.3)"
            }}
          >
            <Camera size={18} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: "#f8fafc" }}>
              Live Geo-Tag Camera
            </Typography>
            <Typography sx={{ fontSize: "0.7rem", color: "#94a3b8" }}>
              {facingMode === "environment" ? "Rear Camera" : "Front Camera"}
              {gpsCoords?.address ? ` • ${gpsCoords.address}` : ""}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Tooltip title="Switch Camera (Flip)">
            <IconButton
              onClick={handleSwitchCamera}
              disabled={isCameraStarting}
              size="small"
              sx={{
                color: "#cbd5e1",
                backgroundColor: "rgba(255,255,255,0.08)",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.15)", color: "#ffffff" }
              }}
            >
              <RotateCw size={17} />
            </IconButton>
          </Tooltip>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              color: "#94a3b8",
              "&:hover": { color: "#ffffff", backgroundColor: "rgba(255,255,255,0.1)" }
            }}
          >
            <X size={20} />
          </IconButton>
        </Box>
      </Box>

      {/* Viewfinder Area */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: { xs: "52vh", sm: "460px" },
          backgroundColor: "#000000",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        {/* Live Video Feed */}
        <video
          ref={(node) => {
            videoRef.current = node;
            if (node && streamRef.current && node.srcObject !== streamRef.current) {
              node.srcObject = streamRef.current;
              node.play().catch(() => {});
            }
          }}
          playsInline
          autoPlay
          muted
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover"
          }}
        />

        {/* Shutter Flash Animation */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundColor: "#ffffff",
            opacity: shutterFlash ? 0.85 : 0,
            transition: "opacity 0.15s ease-out",
            pointerEvents: "none",
            zIndex: 20
          }}
        />

        {/* Camera Starting / Loading Indicator */}
        {isCameraStarting && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              backgroundColor: "rgba(0,0,0,0.75)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
              zIndex: 10
            }}
          >
            <CircularProgress size={36} sx={{ color: "#38bdf8" }} />
            <Typography sx={{ color: "#e2e8f0", fontSize: "0.85rem" }}>
              Initializing camera feed...
            </Typography>
          </Box>
        )}

        {/* Camera Error Message */}
        {cameraError && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              backgroundColor: "rgba(15,23,42,0.92)",
              p: 3,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              zIndex: 15,
              textAlign: "center"
            }}
          >
            <AlertTriangle size={36} color="#ef4444" />
            <Typography sx={{ color: "#f8fafc", fontWeight: 700, fontSize: "0.95rem" }}>
              {cameraError}
            </Typography>
            <Button
              variant="contained"
              onClick={handleClose}
              sx={{
                backgroundColor: "#1877f2",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.82rem"
              }}
            >
              Close Camera
            </Button>
          </Box>
        )}

        {/* Viewfinder Target Framing Guidelines (4 Corner Brackets) */}
        <Box
          sx={{
            position: "absolute",
            inset: { xs: 20, sm: 28 },
            pointerEvents: "none",
            zIndex: 5
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: 24,
              height: 24,
              borderTop: "3px solid rgba(56, 189, 248, 0.9)",
              borderLeft: "3px solid rgba(56, 189, 248, 0.9)",
              borderTopLeftRadius: "6px"
            }}
          />
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              width: 24,
              height: 24,
              borderTop: "3px solid rgba(56, 189, 248, 0.9)",
              borderRight: "3px solid rgba(56, 189, 248, 0.9)",
              borderTopRightRadius: "6px"
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              width: 24,
              height: 24,
              borderBottom: "3px solid rgba(56, 189, 248, 0.9)",
              borderLeft: "3px solid rgba(56, 189, 248, 0.9)",
              borderBottomLeftRadius: "6px"
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              right: 0,
              width: 24,
              height: 24,
              borderBottom: "3px solid rgba(56, 189, 248, 0.9)",
              borderRight: "3px solid rgba(56, 189, 248, 0.9)",
              borderBottomRightRadius: "6px"
            }}
          />
        </Box>

        {/* Floating Top Badge: GPS Lock Status */}
        <Box
          sx={{
            position: "absolute",
            top: 14,
            left: 14,
            backgroundColor: "rgba(15, 23, 42, 0.8)",
            backdropFilter: "blur(6px)",
            border: "1px solid rgba(56, 189, 248, 0.4)",
            borderRadius: "6px",
            px: 1.25,
            py: 0.5,
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            zIndex: 10
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: gpsCoords ? "#22c55e" : "#eab308",
              boxShadow: gpsCoords ? "0 0 8px #22c55e" : "none"
            }}
          />
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#f8fafc" }}>
            {gpsCoords ? "GPS LOCKED" : "ACQUIRING GPS..."}
          </Typography>
        </Box>

        {/* Floating Bottom Live Geotag Watermark Preview */}
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            background:
              "linear-gradient(to top, rgba(15,23,42,0.95) 0%, rgba(15,23,42,0.85) 75%, transparent 100%)",
            pt: 2.5,
            pb: 1.25,
            px: { xs: 2, sm: 2.5 },
            borderTop: "2px solid #38bdf8",
            zIndex: 10
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: "0.75rem", sm: "0.82rem" },
              fontWeight: 800,
              color: "#38bdf8",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              lineHeight: 1.3
            }}
          >
            <MapPin size={13} />
            GPS: {gpsCoords ? `Lat ${gpsCoords.latitude.toFixed(6)}°, Long ${gpsCoords.longitude.toFixed(6)}°` : "Detecting..."}
            {gpsCoords?.accuracy ? ` (±${Math.round(gpsCoords.accuracy)}m)` : ""}
            {gpsCoords?.address ? ` • ${gpsCoords.address}` : ""}
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: "0.68rem", sm: "0.74rem" },
              color: "#ffffff",
              fontWeight: 600,
              lineHeight: 1.3,
              mt: 0.25,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis"
            }}
          >
            🏛️ {instituteName} • {domainTitle}
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: "0.64rem", sm: "0.7rem" },
              color: "#cbd5e1",
              lineHeight: 1.3,
              mt: 0.2
            }}
          >
            🕒 {currentClockTime || "Current Time"} IST
          </Typography>
        </Box>
      </Box>

      {/* Camera Control Footer Bar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: { xs: 2, sm: 3 },
          py: 2,
          backgroundColor: "#0b0f17",
          borderTop: "1px solid rgba(255,255,255,0.08)"
        }}
      >
        {/* Flip Camera Option */}
        <Button
          onClick={handleSwitchCamera}
          disabled={isCameraStarting}
          startIcon={<RotateCw size={15} />}
          sx={{
            color: "#94a3b8",
            textTransform: "none",
            fontSize: "0.78rem",
            fontWeight: 600,
            minWidth: 80,
            "&:hover": { color: "#ffffff" }
          }}
        >
          Flip
        </Button>

        {/* Shutter Button */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Box
            onClick={handleCapture}
            sx={{
              width: 68,
              height: 68,
              borderRadius: "50%",
              border: "4px solid rgba(255,255,255,0.6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: isCapturing ? "default" : "pointer",
              transition: "all 0.15s ease",
              "&:hover": {
                borderColor: "#38bdf8",
                transform: "scale(1.05)"
              },
              "&:active": {
                transform: "scale(0.94)"
              }
            }}
          >
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: "50%",
                backgroundColor: isCapturing ? "#94a3b8" : "#ffffff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#0f172a"
              }}
            >
              {isCapturing ? (
                <CircularProgress size={22} sx={{ color: "#1877f2" }} />
              ) : (
                <Camera size={24} />
              )}
            </Box>
          </Box>
        </Box>

        {/* Cancel / Close Button */}
        <Button
          onClick={handleClose}
          sx={{
            color: "#94a3b8",
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.82rem",
            minWidth: 80,
            "&:hover": { color: "#ffffff" }
          }}
        >
          Cancel
        </Button>
      </Box>
    </Dialog>
  );
};

export default LiveCameraModal;
